import React, { useState, useEffect } from 'react';
import { Snackbar, Alert, AlertColor, Box, Typography, useTheme, useMediaQuery, Slide, SlideProps } from '@mui/material';
import { useNotification, NotificationType } from '../../contexts/NotificationContext';
import { CheckCircle, Error, Info, Warning, Close } from '@mui/icons-material';
import { SwipeableDrawer } from '@mui/material';

// Custom transition for the notification
function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="down" />;
}

const Notification: React.FC = () => {
  const { notification, hideNotification } = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isVerySmallScreen = useMediaQuery('(max-width:400px)');
  
  // Track touch start position for swipe detection
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Handle touch start
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
  };

  // Handle touch move
  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  // Handle touch end
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isUpSwipe = distance > minSwipeDistance;
    
    if (isUpSwipe) {
      hideNotification();
    }
    
    // Reset values
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Map our notification types to Material-UI's AlertColor
  const getSeverity = (type: NotificationType): AlertColor => {
    switch (type) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'info';
    }
  };

  // Get icon based on notification type
  const getIcon = (type: NotificationType) => {
    switch (type) {
      case 'success':
        return <CheckCircle fontSize={isVerySmallScreen ? "small" : "medium"} />;
      case 'error':
        return <Error fontSize={isVerySmallScreen ? "small" : "medium"} />;
      case 'warning':
        return <Warning fontSize={isVerySmallScreen ? "small" : "medium"} />;
      case 'info':
        return <Info fontSize={isVerySmallScreen ? "small" : "medium"} />;
      default:
        return <Info fontSize={isVerySmallScreen ? "small" : "medium"} />;
    }
  };

  // Get background color based on notification type
  const getBackgroundColor = (type: NotificationType) => {
    switch (type) {
      case 'success':
        return theme.palette.success.main;
      case 'error':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'info':
        return theme.palette.info.main;
      default:
        return theme.palette.info.main;
    }
  };

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    hideNotification();
  };

  // Convert hex color to rgba
  const hexToRgba = (hex: string, alpha: number = 0.95): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Get background color for current notification
  const bgColor = hexToRgba(getBackgroundColor(notification.type));

  return (
    <Snackbar
      open={notification.open}
      autoHideDuration={notification.duration}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      TransitionComponent={SlideTransition}
      sx={{
        mt: isMobile ? 2 : 4,
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderRadius: 2,
          px: isVerySmallScreen ? 1.5 : 2,
          py: isVerySmallScreen ? 1 : 1.5,
          width: isMobile ? 'calc(100vw - 32px)' : 'auto',
          maxWidth: '500px',
          minWidth: isMobile ? 'auto' : '300px',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 255, 255, 0.18)',
          background: bgColor,
          animation: 'fadeIn 0.3s ease-out',
          '@keyframes fadeIn': {
            '0%': {
              opacity: 0,
              transform: 'translateY(-20px)'
            },
            '100%': {
              opacity: 1,
              transform: 'translateY(0)'
            }
          },
          position: 'relative',
          '&::after': isMobile ? {
            content: '""',
            position: 'absolute',
            bottom: -8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 40,
            height: 4,
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
            borderRadius: 2,
            transition: 'all 0.2s ease',
          } : {},
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: '#fff',
            mr: 1.5,
          }}
        >
          {getIcon(notification.type)}
        </Box>
        <Typography
          variant={isVerySmallScreen ? "body2" : "body1"}
          sx={{
            color: '#fff',
            fontWeight: 500,
            flexGrow: 1,
            pr: 1,
          }}
        >
          {typeof notification.message === 'string' 
            ? notification.message 
            : 'An error occurred'}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: '#fff',
            cursor: 'pointer',
            opacity: 0.8,
            '&:hover': {
              opacity: 1,
            },
          }}
          onClick={handleClose}
        >
          <Close fontSize={isVerySmallScreen ? "small" : "medium"} />
        </Box>
      </Box>
    </Snackbar>
  );
};

export default Notification; 